// sapi-admin/src/services/whatsappService.js

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config'; // ✅ functions now exists

// ─── Configuration ──────────────────────────────────────────────────────────
// Use environment variables for URLs
// ✅ Ajouter l'URL locale pour le développement
const WHATSAPP_API_URL = process.env.REACT_APP_WHATSAPP_API_URL || 
  (process.env.NODE_ENV === 'development' 
    ? 'https://sendwhatsappordernotification-b3shwrnirq-uc.a.run.app'  // Utiliser la fonction déployée même en dev
    : 'https://sendwhatsappordernotification-b3shwrnirq-uc.a.run.app');

const WHATSAPP_ENEO_URL = process.env.REACT_APP_WHATSAPP_ENEO_URL ||
  'https://sendwhatsappeneorecharge-xxxxx-uc.a.run.app';

const WHATSAPP_PI_SELL_URL = process.env.REACT_APP_WHATSAPP_PI_SELL_URL ||
  'https://sendwhatsapppisell-xxxxx-uc.a.run.app';

// ─── Main Function (via Callable Cloud Function) ───────────────────────────
/**
 * Send a WhatsApp message via Cloud Function (callable)
 * @param {string} phoneNumber - Phone number (international format)
 * @param {string} message - Message content
 * @param {Object} options - Additional options (type, template, etc.)
 * @returns {Promise<Object>} - API response
 */
export const sendWhatsAppMessage = async (phoneNumber, message, options = {}) => {
  try {
    // ✅ Use the correct Callable function name
    const sendMessage = httpsCallable(functions, 'sendWhatsAppMessageCallable');
    const result = await sendMessage({
      to: phoneNumber,
      message: message,
      type: options.type || 'text',
      ...options
    });
    return result.data;
  } catch (error) {
    console.error('❌ WhatsApp send error:', error);
    // Extract Firebase error message
    const errorMessage = error.message || error.details || 'Unknown error';
    throw new Error(errorMessage);
  }
};

// ─── Send Order Notification (via HTTP) ────────────────────────────────────
/**
 * Send an order notification (direct HTTP version)
 * Mainly used from the admin panel
 */
// sapi-admin/src/services/whatsappService.js

// ─── Send Order Notification (via HTTP) ────────────────────────────────────
export const sendWhatsAppOrderUpdate = async (orderId, phoneNumber, options = {}) => {
  try {
    console.log('📤 Envoi WhatsApp à:', phoneNumber);
    console.log('📍 Options:', options);
    
    const response = await fetch(WHATSAPP_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: phoneNumber,
        orderId: orderId,
        status: options.status || 'Placed',
        amount: options.amount || 0,
        items: options.items || [],
        userName: options.userName || 'client',
        // ✅ Ajouter les infos de tracking
        trackingNumber: options.trackingNumber || 'N/A',
        carrier: options.carrier || 'Our delivery partner',
        trackingUrl: options.trackingUrl || 'sapi.etralis.com'
      })
    });
    
    const data = await response.json();
    console.log('📥 Réponse:', data);
    return data;
  } catch (error) {
    console.error('❌ WhatsApp order update error:', error);
    return { success: false, error: error.message };
  }
};

// ─── Status Notifications (via Callable) ──────────────────────────────────
/**
 * Send an order notification based on status
 * @param {Object} order - Order data
 * @param {string} status - New status
 */
export const sendOrderStatusNotification = async (order, status) => {
  const statusMessages = {
    'Placed': `🛒 New order received!\n\nOrder #${order.orderId}\nAmount: ${FormatPrice(order.total || order.totalAmount)} π\nCustomer: ${order.userName || 'Customer'}\n\nThank you for your trust!`,
    
    'Approved': `✅ Order #${order.orderId} approved!\n\nYour order is being prepared.\nWe will keep you informed of its progress.`,
    
    'Shipped': `📦 Order #${order.orderId} shipped!\n\nYour package is on its way!\nTracking number: ${order.tracking?.code || 'N/A'}\n\nTrack your package on our website.`,
    
    'Delivered': `🎉 Order #${order.orderId} delivered!\n\nYour order has been successfully delivered.\nWe hope you are satisfied!\n\nFeel free to leave us a review.`,
    
    'Cancelled': `❌ Order #${order.orderId} cancelled\n\nYour order has been cancelled.\nA refund will be processed within 48 hours.`,
    
    'Declined': `❌ Order #${order.orderId} declined\n\nYour order could not be processed.\nPlease contact support for more information.`,
  };

  const message = statusMessages[status] || `Your order #${order.orderId} status has been updated: ${status}`;
  
  if (order.userPhone || order.phoneNumber) {
    const phone = order.userPhone || order.phoneNumber;
    return await sendWhatsAppMessage(phone, message);
  }
  return { success: false, error: 'No phone number available' };
};

// ─── ENEO Recharge Notification ───────────────────────────────────────────
/**
 * Send an ENEO recharge notification (via HTTP)
 */
export const sendWhatsAppEneoRecharge = async (phoneNumber, rechargeData) => {
  try {
    const response = await fetch(WHATSAPP_ENEO_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: phoneNumber,
        meterNumber: rechargeData.meterNumber,
        kWh: rechargeData.kWh,
        amountFCFA: rechargeData.amountFCFA,
        amountPi: rechargeData.amountPi,
        token: rechargeData.token,
        userName: rechargeData.userName || 'customer'
      })
    });
    return await response.json();
  } catch (error) {
    console.error('❌ WhatsApp ENEO error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send an ENEO recharge notification (via Callable)
 */
export const sendEneoRechargeNotification = async (transaction) => {
  const message = `⚡ ENEO recharge completed!\n\nMeter: ${transaction.meterNumber}\nAmount: ${transaction.amountPi} π\nEnergy: ${transaction.kWh} kWh\nToken: ${transaction.token}\n\nThank you for using Sapi!`;
  
  const phone = transaction.userPhone || transaction.phoneNumber;
  if (phone) {
    return await sendWhatsAppMessage(phone, message);
  }
  return { success: false, error: 'No phone number available' };
};

// ─── Pi Sale Notification ─────────────────────────────────────────────────
/**
 * Send a Pi sale notification (via HTTP)
 */
// ─── Pi Sale Notification ─────────────────────────────────────────────────
export const sendWhatsAppPiSell = async (phoneNumber, piSellData) => {
  try {
    const response = await fetch(WHATSAPP_PI_SELL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: phoneNumber,
        piAmount: piSellData.piAmount,
        fcfaAmount: piSellData.fcfaAmount,
        rate: piSellData.rate,
        status: piSellData.status || 'pending_manual',
        userName: piSellData.userName || 'customer'
      })
    });
    return await response.json();
  } catch (error) {
    console.error('❌ WhatsApp Pi Sell error:', error);
    return { success: false, error: error.message };
  }
};

export const sendPiSellNotification = async (piSellRequest, status) => {
  const messages = {
    'pending_manual': `💰 Pi sale request received!\n\nAmount: ${piSellRequest.piAmount} π\nRate: ${piSellRequest.rate} FCFA/π\nTotal: ${piSellRequest.fcfaAmount} FCFA\n\nWe are processing your request as quickly as possible.`,
    'completed': `✅ Pi sale confirmed!\n\n${piSellRequest.piAmount} π sold\nAmount received: ${piSellRequest.fcfaAmount} FCFA\n\nTransaction completed successfully!`,
    'cancelled': `❌ Pi sale cancelled\n\nYour sale request has been cancelled.\nYour Pi has been returned.`,
  };

  const message = messages[status] || `Your Pi sale request is ${status}`;
  const phone = piSellRequest.phoneNumber || piSellRequest.userPhone;
  if (phone) {
    return await sendWhatsAppMessage(phone, message);
  }
  return { success: false, error: 'No phone number available' };
};

// ─── Pi Store Notification ────────────────────────────────────────────────
/**
 * Send a Pi store activation notification
 */
export const sendStoreActivationNotification = async (store) => {
  const message = `🏪 Your Pi Store is now active!\n\n${store.name}\n${store.description || ''}\n\nYour store is visible to all Sapi users.\n\nHappy selling! 🚀`;
  
  const phone = store.contact?.phone || store.phoneNumber;
  if (phone) {
    return await sendWhatsAppMessage(phone, message);
  }
  return { success: false, error: 'No phone number available' };
};

// ─── Admin Notification ───────────────────────────────────────────────────
/**
 * Send an admin notification (alert)
 */
export const sendAdminAlert = async (message, adminPhone = process.env.REACT_APP_ADMIN_PHONE) => {
  if (!adminPhone) {
    console.warn('⚠️ Admin phone number not configured');
    return { success: false, error: 'Admin phone not configured' };
  }
  return await sendWhatsAppMessage(adminPhone, `🔔 ADMIN ALERT\n\n${message}`);
};

// ─── Utility Function for Pi Formatting ───────────────────────────────────
/**
 * Format a Pi amount for display
 */
const FormatPrice = (price) => {
  if (price === undefined || price === null) return '0 π';
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(num)) return '0 π';
  const fixed = num.toFixed(4);
  const trimmed = fixed.replace(/\.?0+$/, '');
  return `${trimmed} π`;
};

// ─── Envoyer un template WhatsApp ───────────────────────────────────────────
export const sendWhatsAppTemplate = async (phoneNumber, templateName, params, languageCode = 'en') => {
  try {
    const cleanNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length < 10) {
      return { success: false, error: 'Numéro invalide' };
    }

    const response = await fetch('https://sendwhatsappmessage-b3shwrnirq-uc.a.run.app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: cleanNumber,
        type: 'template',
        templateName: templateName,
        templateParams: params,
        languageCode: languageCode
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ WhatsApp template error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Envoyer une notification de commande avec template
 */
export const sendOrderTemplate = async (order, eventType = 'placed') => {
  const templates = {
    placed: 'order_confirmation',
    shipped: 'order_shipped',
    delivered: 'order_delivered',
    delayed: 'delivery_delay'
  };
  
  const templateName = templates[eventType] || templates.placed;
  
  const variables = {
    var1: order.userName || 'Client',
    var2: order.orderId || order.id,
    var3: (order.totalAmount || order.total || 0).toString(),
    var4: order.shipping?.method || 'Standard',
    var5: order.tracking?.code || 'N/A',
    var6: order.tracking?.estimatedDelivery || '2-3 jours',
    var7: `https://sapi.cm/tracking/${order.orderId}`
  };
  
  return sendWhatsAppTemplate(order.phoneNumber, templateName, variables);
};

/**
 * Envoyer une notification de recharge ENEO
 */
export const sendEneoTemplate = async (transaction) => {
  const variables = {
    var1: transaction.userName || 'Client',
    var2: transaction.meterNumber,
    var3: transaction.kWh.toString(),
    var4: transaction.amountPi.toString(),
    var5: transaction.token || 'N/A'
  };
  
  return sendWhatsAppTemplate(transaction.userPhone, 'eneo_recharge', variables);
};

/**
 * Envoyer une notification de vente Pi
 */
export const sendPiSellTemplate = async (request, status = 'pending') => {
  const templateName = status === 'completed' ? 'pi_sell_confirmed' : 'pi_sell_pending';
  
  const variables = {
    var1: request.userName || 'Client',
    var2: request.piAmount.toString(),
    var3: request.rate?.toString() || '500',
    var4: request.fcfaAmount?.toString() || '0'
  };
  
  return sendWhatsAppTemplate(request.phoneNumber, templateName, variables);
};

// sapi-admin/src/services/whatsappService.js

// ─── Envoyer notification Packed ───────────────────────────────────────────
export const sendOrderPackedNotification = async (order) => {
  const templateName = 'order_packed';
  const params = [
    order.userName || 'Client',
    order.orderId || order.id
  ];
  
  const phone = order.userPhone || order.phoneNumber;
  if (!phone) {
    return { success: false, error: 'No phone number available' };
  }
  
  return await sendWhatsAppTemplate(phone, templateName, params);
};

// ─── Default Export ───────────────────────────────────────────────────────
export default {
  sendWhatsAppMessage,
  sendWhatsAppOrderUpdate,
  sendOrderStatusNotification,
  sendWhatsAppEneoRecharge,
  sendEneoRechargeNotification,
  sendWhatsAppPiSell,
  sendPiSellNotification,
  sendStoreActivationNotification,
  sendAdminAlert,
  sendWhatsAppTemplate,
  sendOrderTemplate,
  sendEneoTemplate,
  sendPiSellTemplate,
  sendOrderPackedNotification
};